import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  Cloud,
  ExternalLink,
  FileKey2,
  Files,
  KeyRound,
  Mail,
  Settings2,
  ShieldCheck,
  Users,
  X,
} from 'lucide-react';

interface GoogleWorkspaceApiSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'overview' | 'apis' | 'consent' | 'credentials' | 'admin' | 'validation';

const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'overview', label: 'Vue d’ensemble', icon: Cloud },
  { id: 'apis', label: 'API', icon: Settings2 },
  { id: 'consent', label: 'Consentement OAuth', icon: Users },
  { id: 'credentials', label: 'Identifiants', icon: KeyRound },
  { id: 'admin', label: 'Administration', icon: ShieldCheck },
  { id: 'validation', label: 'Validation', icon: CheckCircle2 },
];

const links = {
  projects: 'https://console.cloud.google.com/projectselector2/home/dashboard',
  library: 'https://console.cloud.google.com/apis/library',
  auth: 'https://console.cloud.google.com/auth/overview',
  audience: 'https://console.cloud.google.com/auth/audience',
  credentials: 'https://console.cloud.google.com/apis/credentials',
  admin: 'https://admin.google.com/ac/owl/list?tab=configuredApps',
};

function Panel({ title, icon: Icon, children, tone = 'blue' }: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  tone?: 'blue' | 'amber' | 'emerald' | 'rose';
}) {
  const tones = {
    blue: 'border-blue-300/20 bg-blue-400/5',
    amber: 'border-amber-300/25 bg-amber-400/10',
    emerald: 'border-emerald-300/20 bg-emerald-400/5',
    rose: 'border-rose-300/25 bg-rose-400/10',
  };

  return (
    <section className={`rounded-2xl border p-5 ${tones[tone]}`}>
      <h3 className="mb-4 flex items-center gap-2 text-lg font-black text-white">
        <Icon className="h-5 w-5 text-blue-300" />
        {title}
      </h3>
      {children}
    </section>
  );
}

function ExternalButton({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank" rel="noreferrer"
      className="inline-flex items-center gap-2 rounded-xl bg-blue-400 px-4 py-2.5 text-sm font-black text-slate-950 transition hover:bg-blue-300"
    >
      {children}
      <ExternalLink className="h-4 w-4" />
    </a>
  );
}

function Steps({ items }: { items: React.ReactNode[] }) {
  return (
    <ol className="space-y-3 text-sm leading-6 text-slate-300">
      {items.map((item, index) => (
        <li key={index} className="flex gap-3 rounded-xl border border-white/10 bg-black/20 p-4">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-400 font-black text-slate-950">
            {index + 1}
          </span>
          <span>{item}</span>
        </li>
      ))}
    </ol>
  );
}

function Bullets({ items }: { items: React.ReactNode[] }) {
  return (
    <ul className="space-y-2 text-sm leading-6 text-slate-300">
      {items.map((item, index) => (
        <li key={index} className="flex gap-2">
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-400" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function OverviewTab() {
  return (
    <div className="space-y-5">
      <Panel title="Objectif" icon={Cloud}>
        <p className="text-sm leading-6 text-slate-300">
          Préparer un nouveau projet Google Cloud afin qu’une application autorisée puisse envoyer des courriels et utiliser Google Calendar, Google Drive et Google Docs au moyen d’OAuth 2.0.
        </p>
      </Panel>

      <Panel title="Parcours complet" icon={CheckCircle2} tone="emerald">
        <div className="grid gap-3 md:grid-cols-5">
          {['Créer le projet', 'Activer les API', 'Configurer OAuth', 'Créer le client', 'Autoriser et tester'].map((label, index) => (
            <div key={label} className="rounded-xl border border-white/10 bg-black/20 p-4 text-center">
              <span className="mx-auto flex h-8 w-8 items-center justify-center rounded-full bg-emerald-400 font-black text-slate-950">{index + 1}</span>
              <p className="mt-2 text-sm font-bold text-white">{label}</p>
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="Créer ou sélectionner le projet" icon={Settings2}>
        <Steps items={[
          <>Ouvrir le sélecteur de projets Google Cloud.</>,
          <>Créer un projet, par exemple <strong className="text-white">Hermes-Google-Workspace</strong>, ou sélectionner le projet prévu.</>,
          <>Confirmer que ce projet apparaît dans la barre supérieure avant de poursuivre.</>,
        ]} />
        <div className="mt-4"><ExternalButton href={links.projects}>Ouvrir les projets Google Cloud</ExternalButton></div>
      </Panel>
    </div>
  );
}

const apiRows = [
  { name: 'Gmail API', need: 'Envoyer, consulter ou répondre aux courriels', required: true, icon: Mail },
  { name: 'Google Calendar API', need: 'Consulter et gérer les calendriers et événements', required: true, icon: CalendarDays },
  { name: 'Google Drive API', need: 'Téléverser, télécharger, rechercher et partager les fichiers', required: true, icon: Files },
  { name: 'Google Docs API', need: 'Créer, lire et modifier les documents Google Docs natifs', required: true, icon: FileKey2 },
  { name: 'Google Sheets API', need: 'Gérer des feuilles Google Sheets, si nécessaire', required: false, icon: Files },
  { name: 'People API', need: 'Accéder aux contacts Google, si nécessaire', required: false, icon: Users },
];

function ApisTab() {
  return (
    <div className="space-y-5">
      <Panel title="Activer les services requis" icon={Settings2}>
        <p className="mb-4 text-sm leading-6 text-slate-300">Dans la bibliothèque, rechercher chaque API par son nom exact, ouvrir sa fiche, puis cliquer sur <strong className="text-white">Enable / Activer</strong>.</p>
        <div className="grid gap-3 md:grid-cols-2">
          {apiRows.map(({ name, need, required, icon: Icon }) => (
            <article key={name} className="rounded-xl border border-white/10 bg-black/20 p-4">
              <div className="flex items-start gap-3">
                <Icon className="mt-0.5 h-5 w-5 shrink-0 text-blue-300" />
                <div>
                  <p className="font-black text-white">{name}</p>
                  <p className="mt-1 text-sm leading-6 text-slate-300">{need}</p>
                  <span className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-[11px] font-black ${required ? 'bg-emerald-400/15 text-emerald-200' : 'bg-slate-400/15 text-slate-300'}`}>{required ? 'REQUIS' : 'OPTIONNEL'}</span>
                </div>
              </div>
            </article>
          ))}
        </div>
        <div className="mt-4"><ExternalButton href={links.library}>Ouvrir la bibliothèque des API</ExternalButton></div>
      </Panel>

      <Panel title="Différence importante pour les fichiers .docx" icon={Files} tone="amber">
        <Bullets items={[
          <>La <strong className="text-white">Google Drive API</strong> sert à téléverser, télécharger et partager un vrai fichier <code>.docx</code>.</>,
          <>La <strong className="text-white">Google Docs API</strong> sert aux documents Google Docs natifs et à leur contenu.</>,
          <>Pour convertir entre Word et Google Docs, prévoir Google Drive API et valider explicitement le format produit après conversion.</>,
        ]} />
      </Panel>
    </div>
  );
}

function ConsentTab() {
  return (
    <div className="space-y-5">
      <Panel title="Configurer l’écran de consentement OAuth" icon={Users}>
        <Steps items={[
          <>Ouvrir <strong className="text-white">Google Auth Platform</strong> dans le bon projet.</>,
          <>Dans <strong className="text-white">Branding</strong>, définir le nom de l’application, par exemple <strong className="text-white">Hermes Workspace</strong>, et les coordonnées demandées.</>,
          <>Dans <strong className="text-white">Audience</strong>, choisir le type d’utilisateurs autorisés.</>,
          <>Ajouter uniquement les portées OAuth nécessaires aux fonctions réellement utilisées.</>,
        ]} />
        <div className="mt-4 flex flex-wrap gap-3">
          <ExternalButton href={links.auth}>Ouvrir Google Auth Platform</ExternalButton>
          <ExternalButton href={links.audience}>Ouvrir Audience</ExternalButton>
        </div>
      </Panel>

      <div className="grid gap-5 md:grid-cols-2">
        <Panel title="Internal / Interne" icon={ShieldCheck} tone="emerald">
          <p className="text-sm leading-6 text-slate-300">Choisir ce mode si l’application est réservée aux utilisateurs de la même organisation Google Workspace. C’est généralement le meilleur choix pour un outil interne d’entreprise.</p>
        </Panel>
        <Panel title="External / Externe" icon={Users}>
          <p className="text-sm leading-6 text-slate-300">Choisir ce mode si des comptes situés hors de l’organisation doivent autoriser l’application. Des exigences de vérification Google peuvent alors s’appliquer.</p>
        </Panel>
      </div>

      <Panel title="Application en mode Testing" icon={AlertTriangle} tone="amber">
        <p className="text-sm leading-6 text-amber-100">Si l’audience externe est encore en test, ajouter les comptes autorisés dans <strong>Audience → Test users → Add users</strong>. Sans cette étape, l’utilisateur de test peut être bloqué pendant l’autorisation OAuth.</p>
      </Panel>
    </div>
  );
}

function CredentialsTab() {
  return (
    <div className="space-y-5">
      <Panel title="Créer le client OAuth" icon={KeyRound}>
        <Steps items={[
          <>Ouvrir <strong className="text-white">APIs & Services → Credentials</strong>.</>,
          <>Cliquer <strong className="text-white">Create credentials</strong>, puis <strong className="text-white">OAuth client ID</strong>.</>,
          <>Choisir le type d’application <strong className="text-white">Desktop app</strong>.</>,
          <>Donner un nom explicite, par exemple <strong className="text-white">Hermes Desktop OAuth</strong>, puis créer le client.</>,
          <>Télécharger le fichier JSON fourni par Google.</>,
        ]} />
        <div className="mt-4"><ExternalButton href={links.credentials}>Ouvrir les identifiants Google Cloud</ExternalButton></div>
      </Panel>

      <Panel title="Fichier téléchargé" icon={FileKey2} tone="emerald">
        <div className="rounded-xl border border-slate-700 bg-slate-950 p-4 font-mono text-sm text-emerald-300">client_secret_123456789.apps.googleusercontent.com.json</div>
        <Bullets items={[
          <>Le nom réel varie selon le projet et le Client ID.</>,
          <>Le fichier contient notamment le <strong className="text-white">Client ID</strong> et le <strong className="text-white">Client Secret</strong>.</>,
          <>Le conserver dans un emplacement privé, hors du dépôt Git, des captures et de PRIsME.</>,
          <>Partager seulement son chemin local avec l’agent chargé de la configuration, jamais son contenu dans une conversation.</>,
        ]} />
      </Panel>

      <Panel title="Variables d’environnement Google" icon={KeyRound}>
        <p className="mb-4 text-sm leading-6 text-slate-300">
          Ajouter ces variables dans le fichier <code className="text-white">.env</code> de l’application. Dans <code className="text-white">.env.example</code>, conserver seulement les noms avec des valeurs vides.
        </p>
        <pre className="overflow-x-auto rounded-xl border border-slate-700 bg-slate-950 p-4 text-sm text-emerald-300"><code>{`# Google Cloud / OAuth
GOOGLE_CLIENT_ID=
GOOGLE_CLIENT_SECRET=
GOOGLE_PROJECT_ID=`}</code></pre>
        <div className="mt-4">
          <Bullets items={[
            <><strong className="text-white">GOOGLE_CLIENT_ID</strong> : dans Google Cloud, ouvrir <strong className="text-white">API et services → Identifiants → ID clients OAuth 2.0</strong>, puis copier l’ID du client créé.</>,
            <><strong className="text-white">GOOGLE_CLIENT_SECRET</strong> : au même endroit, ouvrir le client OAuth et copier le secret, ou utiliser la valeur <code className="text-white">client_secret</code> du fichier JSON téléchargé.</>,
            <><strong className="text-white">GOOGLE_PROJECT_ID</strong> : ouvrir le sélecteur de projet ou le tableau de bord Google Cloud et copier l’<strong className="text-white">ID du projet</strong>. Ne pas confondre avec le nom ni le numéro du projet.</>,
            <>Ne jamais inscrire les vraies valeurs dans <code className="text-white">.env.example</code>, Git, une capture d’écran ou une conversation.</>,
          ]} />
        </div>
      </Panel>
    </div>
  );
}

function AdminTab() {
  return (
    <div className="space-y-5">
      <Panel title="Autoriser l’application dans Google Workspace" icon={ShieldCheck}>
        <p className="mb-4 text-sm leading-6 text-slate-300">Si l’organisation restreint les applications OAuth, un super-administrateur doit examiner le client dans <strong className="text-white">Sécurité → Contrôle des accès et des données → Contrôles des API</strong>.</p>
        <Steps items={[
          <>Ouvrir la liste des applications configurées dans la console d’administration.</>,
          <>Ajouter ou rechercher le <strong className="text-white">Client ID OAuth</strong> créé précédemment.</>,
          <>Examiner les API et portées demandées; retirer celles qui ne sont pas nécessaires.</>,
          <>Marquer l’application comme <strong className="text-white">Trusted / Approuvée</strong>, ou limiter l’accès aux services nécessaires selon la politique interne.</>,
        ]} />
        <div className="mt-4"><ExternalButton href={links.admin}>Ouvrir les contrôles des API</ExternalButton></div>
      </Panel>

      <Panel title="Quand cette étape est souvent nécessaire" icon={AlertTriangle} tone="amber">
        <Bullets items={[
          <>L’organisation bloque les applications tierces ou les portées OAuth sensibles.</>,
          <>Une protection avancée, une règle de confiance ou une clé de sécurité est imposée.</>,
          <>L’autorisation affiche « accès bloqué », « application non approuvée » ou une erreur liée à la politique Workspace.</>,
        ]} />
      </Panel>
    </div>
  );
}

function ValidationTab() {
  return (
    <div className="space-y-5">
      <Panel title="Validation de bout en bout" icon={CheckCircle2} tone="emerald">
        <Steps items={[
          <>Confirmer que Gmail API, Google Calendar API, Google Drive API et Google Docs API sont activées dans le bon projet.</>,
          <>Confirmer que l’audience et les Test users correspondent aux comptes qui autoriseront l’application.</>,
          <>Lancer le flux OAuth depuis l’application, ouvrir le lien d’autorisation Google et approuver uniquement les portées prévues.</>,
          <>Tester réellement l’envoi d’un courriel avec Gmail API.</>,
          <>Créer ou lire un événement avec Google Calendar API.</>,
          <>Téléverser puis relire un fichier avec Google Drive API.</>,
          <>Créer ou lire un document Google Docs avec Google Docs API.</>,
        ]} />
      </Panel>

      <Panel title="Critère de réussite" icon={CheckCircle2}>
        <p className="text-sm leading-6 text-slate-300">La configuration est terminée seulement lorsque les quatre tests réels réussissent avec le compte attendu. Le simple fait d’avoir téléchargé le JSON ou activé les API ne confirme pas que l’autorisation OAuth fonctionne.</p>
      </Panel>

      <Panel title="Sécurité et dépannage" icon={AlertTriangle} tone="rose">
        <Bullets items={[
          <>Erreur <strong className="text-white">access blocked</strong> : vérifier l’audience, les Test users et les contrôles des API Workspace.</>,
          <>Erreur <strong className="text-white">API not enabled</strong> : confirmer le projet associé au Client ID et activer l’API indiquée.</>,
          <>Erreur de portée : demander seulement les permissions nécessaires et refaire l’autorisation après modification.</>,
          <>Secret exposé : supprimer ou révoquer immédiatement le client OAuth, en créer un nouveau et remplacer le fichier local.</>,
          <>Ne jamais déposer le JSON OAuth, un jeton d’accès ou un jeton de rafraîchissement dans Git.</>,
        ]} />
      </Panel>
    </div>
  );
}

export default function GoogleWorkspaceApiSetupModal({ isOpen, onClose }: GoogleWorkspaceApiSetupModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const previouslyFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    setActiveTab('overview');
    const focusFrame = window.requestAnimationFrame(() => dialogRef.current?.focus());

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'));
      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (document.activeElement === dialogRef.current || !dialogRef.current.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
      previouslyFocusedElement?.focus();
    };
  }, [isOpen, onClose]);

  const content = {
    overview: <OverviewTab />,
    apis: <ApisTab />,
    consent: <ConsentTab />,
    credentials: <CredentialsTab />,
    admin: <AdminTab />,
    validation: <ValidationTab />,
  }[activeTab];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/85 p-3 backdrop-blur-sm md:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={onClose}
          role="dialog"
          aria-modal="true"
          aria-labelledby="google-workspace-api-modal-title"
        >
          <motion.div
            ref={dialogRef}
            tabIndex={-1}
            className="flex max-h-[94vh] w-full max-w-7xl flex-col overflow-hidden rounded-3xl border border-blue-300/20 bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 shadow-2xl outline-none"
            initial={{ y: 30, scale: 0.98 }}
            animate={{ y: 0, scale: 1 }}
            exit={{ y: 20, scale: 0.98 }}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="relative flex shrink-0 items-start justify-between gap-4 overflow-hidden border-b border-white/10 bg-black/20 px-5 py-5 md:px-7">
              <div className="absolute inset-x-0 top-0 flex h-1"><span className="flex-1 bg-[#4285f4]" /><span className="flex-1 bg-[#ea4335]" /><span className="flex-1 bg-[#fbbc05]" /><span className="flex-1 bg-[#34a853]" /></div>
              <div className="flex gap-4">
                <div className="rounded-2xl bg-blue-400/10 p-3"><Cloud className="h-7 w-7 text-blue-300" /></div>
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.25em] text-blue-300">Procédure interne BZ</p>
                  <h2 id="google-workspace-api-modal-title" className="mt-1 text-2xl font-black text-white md:text-3xl">Configuration API Google Workspace</h2>
                  <p className="mt-1 text-sm text-slate-300">Gmail · Calendar · Drive · Docs · OAuth 2.0</p>
                </div>
              </div>
              <button type="button" onClick={onClose} className="rounded-xl border border-white/10 bg-white/5 p-2 text-slate-300 hover:bg-white/10 hover:text-white" aria-label="Fermer"><X className="h-5 w-5" /></button>
            </header>

            <nav role="tablist" className="flex shrink-0 gap-2 overflow-x-auto border-b border-white/10 px-4 py-3 md:px-7" aria-label="Sections de la procédure API Google Workspace">
              {tabs.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  id={`google-workspace-api-tab-${id}`}
                  role="tab"
                  aria-selected={activeTab === id}
                  aria-controls={`google-workspace-api-panel-${id}`}
                  tabIndex={activeTab === id ? 0 : -1}
                  type="button"
                  onClick={() => setActiveTab(id)}
                  className={`flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-xs font-bold transition ${activeTab === id ? 'bg-blue-400 text-slate-950' : 'bg-white/5 text-slate-300 hover:bg-white/10'}`}
                >
                  <Icon className="h-4 w-4" />{label}
                </button>
              ))}
            </nav>

            <div id={`google-workspace-api-panel-${activeTab}`} role="tabpanel" aria-labelledby={`google-workspace-api-tab-${activeTab}`} className="overflow-y-auto p-4 md:p-7">
              {content}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
